import { create } from "zustand";
import {
  type Session,
  type SessionMessage,
  type EncryptedKeysBlob,
  type AttachedDocument,
  DOCUMENT_HISTORY_MAX,
  ATTACHED_DOCUMENT_MAX_BYTES,
  ATTACHED_DOCUMENTS_TOTAL_MAX_BYTES,
  newSession,
  appendMessage,
} from "../lib/session";
import { writeAndCommit, deleteFile, commit as gitCommit } from "../lib/git-store";

// Multi-channel state. `channels` is the full list (in display order,
// newest activity first); `activeChannelId` points at the one currently
// shown. Both persist to localStorage on every change.
//
// Session-the-type lives in lib/session; Channel-the-UI-noun is just
// how the sidebar presents Sessions. The portable JSON artifact is
// still a Session — that name stays in the file format.
//
// Migration from v0 (single `playground.current_session` key): on first
// load the old key is read, seeded as the first channel, and removed.

const STORAGE_CHANNELS  = "playground.channels";
const STORAGE_ACTIVE_ID = "playground.active_channel_id";
const STORAGE_V0_LEGACY = "playground.current_session";

function loadChannels(): Session[] {
  if (typeof localStorage === "undefined") return [];

  // v0 migration: if the legacy single-session key exists and the new
  // key doesn't, seed channels from it and clear the legacy key.
  const newRaw = localStorage.getItem(STORAGE_CHANNELS);
  if (!newRaw) {
    const legacyRaw = localStorage.getItem(STORAGE_V0_LEGACY);
    if (legacyRaw) {
      try {
        const legacy = JSON.parse(legacyRaw) as Session;
        const seeded = [legacy];
        localStorage.setItem(STORAGE_CHANNELS, JSON.stringify(seeded));
        localStorage.setItem(STORAGE_ACTIVE_ID, legacy.id);
        localStorage.removeItem(STORAGE_V0_LEGACY);
        return seeded;
      } catch {
        localStorage.removeItem(STORAGE_V0_LEGACY);
      }
    }
    return [];
  }

  try {
    const arr = JSON.parse(newRaw);
    return Array.isArray(arr) ? (arr as Session[]) : [];
  } catch {
    return [];
  }
}

function loadActiveId(channels: Session[]): string | null {
  if (typeof localStorage === "undefined") return null;
  const stored = localStorage.getItem(STORAGE_ACTIVE_ID);
  if (stored && channels.some((c) => c.id === stored)) return stored;
  return channels[0]?.id ?? null;
}

function persistChannels(channels: Session[]): void {
  if (typeof localStorage === "undefined") return;
  localStorage.setItem(STORAGE_CHANNELS, JSON.stringify(channels));
}

function persistActiveId(id: string | null): void {
  if (typeof localStorage === "undefined") return;
  if (id) localStorage.setItem(STORAGE_ACTIVE_ID, id);
  else localStorage.removeItem(STORAGE_ACTIVE_ID);
}

// ─── Git-canonical write-through ─────────────────────────────────────────────
//
// Every channel mutation also writes the full channel JSON to git at
// data/channels/<id>.json and commits. Fire-and-forget so the UI
// doesn't block on the in-browser git layer; failures log but don't
// throw (the localStorage projection is still authoritative for reads
// during Phase 2). Phase 3's auto-push will catch any rolling
// commits up to GitHub.

function shortId(id: string): string {
  return id.slice(0, 8);
}

function gitWriteChannel(channel: Session, message: string): void {
  const path = `data/channels/${channel.id}.json`;
  const body = JSON.stringify(channel, null, 2);
  void writeAndCommit(path, body, message).catch((e) => {
    console.warn("[git-store] channel write failed:", channel.id, e);
  });
}

function gitDeleteChannel(channelId: string, title: string): void {
  const path = `data/channels/${channelId}.json`;
  void (async () => {
    try {
      const removed = await deleteFile(path);
      if (removed) await gitCommit(`delete channel ${shortId(channelId)} (${title})`);
    } catch (e) {
      console.warn("[git-store] channel delete failed:", channelId, e);
    }
  })();
}

type SessionState = {
  channels: Session[];
  activeChannelId: string | null;

  /** Convenience accessor — the currently active channel (or null). */
  activeChannel(): Session | null;

  /** Create a new channel and switch to it. Caller supplies the
   *  creator's display name (typically from authStore.user.name). */
  createChannel: (title: string, creatorName: string) => Session;

  /** Add an imported session as a new channel and switch to it. */
  addChannel: (session: Session) => void;

  /** Switch to a channel by id. */
  selectChannel: (id: string) => void;

  /** Delete a channel by id; if it was active, switch to the next available. */
  deleteChannel: (id: string) => void;

  /** Append a message to the active channel. */
  append: (message: Omit<SessionMessage, "id" | "created_at">) => void;

  /** Attach (or replace) an encrypted-keys blob on a channel. */
  setEncryptedKeys: (channelId: string, blob: EncryptedKeysBlob | null) => void;

  /** Set or clear the GitHub sync target for a channel. */
  setSyncTarget: (channelId: string, target: Session["sync_target"] | null) => void;

  /** Toggle the tools/agent loop for a channel. */
  setToolsEnabled: (channelId: string, enabled: boolean) => void;

  /** Replace a channel's entire session (used after a remote merge).
   *  Preserves the channel's position in the list. */
  replaceChannel: (channelId: string, session: Session) => void;

  /** Toggle a channel between "chat" and "brainstorm" modes. The
   *  brainstorm mode renders the Document lens beside the chat. */
  setChannelMode: (channelId: string, mode: "chat" | "brainstorm") => void;

  /** Replace a channel's whiteboard document (markdown). Used by
   *  brainstorm-mode AI rewrites and by manual user edits in the
   *  DocumentPanel. The prior value is pushed onto document_history
   *  so users can Undo bad rewrites. */
  setChannelDocument: (channelId: string, document: string) => void;

  /** Pop the most recent document_history snapshot back into
   *  document. No-op when history is empty. The popped entry is
   *  removed (no redo). */
  undoDocument: (channelId: string) => void;

  /** Toggle the brainstorm Mosh-Pit flag on a channel. Distinct from
   *  the chat-side mosh_pit_enabled — brainstorm rewrites are much
   *  bigger per call. */
  setBrainstormMoshPit: (channelId: string, on: boolean) => void;

  /** Bump the running token totals on a channel after an AI
   *  dispatch. Pass 0/0 to record a turn without token data (still
   *  bumps `turns`). Used by the toolbar's cost meter. */
  recordUsage: (channelId: string, prompt: number, completion: number) => void;

  /** Toggle the pinned flag on a channel. Pinned channels stay at
   *  the top of the sidebar above the activity-sorted ones. */
  togglePinned: (channelId: string) => void;

  /** Toggle the archived flag on a channel. Archived channels are
   *  hidden from the default sidebar view but reachable via the
   *  "show archived" toggle. */
  toggleArchived: (channelId: string) => void;

  /** Toggle a user's reaction on a message in the active channel.
   *  If the user already reacted with this emoji, removes them;
   *  otherwise adds them. Emoji-with-empty-array prunes itself. */
  toggleReaction: (channelId: string, messageId: string, emoji: string, userName: string) => void;

  /** Create a deep copy of a channel as a new channel. Keeps
   *  configuration AND content (messages, whiteboard, attached docs)
   *  but resets identity (new id + timestamps), local navigation
   *  state (pin, sync target, encrypted keys, token totals,
   *  document history), and the title (suffixed with " — copy").
   *  Different from a template (which strips content) — duplicate
   *  is for forking a channel mid-conversation. */
  duplicateChannel: (channelId: string) => void;

  /** Attach a library document to a channel. Copies content into
   *  session.attached_documents so multiplayer teammates see the
   *  same context. Throws if it would exceed the per-doc or per-
   *  channel byte caps. */
  attachDocument: (channelId: string, name: string, content: string, attachedBy: string) => void;

  /** Detach a doc from a channel by its attached-doc id (not the
   *  library doc id — those can differ across re-attachments). */
  detachDocument: (channelId: string, attachedId: string) => void;
};

const initialChannels = loadChannels();
const initialActiveId = loadActiveId(initialChannels);

export const useSessionStore = create<SessionState>((set, get) => ({
  channels: initialChannels,
  activeChannelId: initialActiveId,
  activeChannel: () => {
    const { channels, activeChannelId } = get();
    return channels.find((c) => c.id === activeChannelId) ?? null;
  },

  createChannel: (title, creatorName) => {
    const s = newSession(title, creatorName);
    const next = [s, ...get().channels];
    persistChannels(next);
    persistActiveId(s.id);
    set({ channels: next, activeChannelId: s.id });
    gitWriteChannel(s, `create channel ${shortId(s.id)} (${title})`);
    return s;
  },

  addChannel: (session) => {
    // Dedupe by id — re-importing the same session is a no-op except
    // for switching to it.
    const existing = get().channels.find((c) => c.id === session.id);
    if (existing) {
      persistActiveId(session.id);
      set({ activeChannelId: session.id });
      return;
    }
    const next = [session, ...get().channels];
    persistChannels(next);
    persistActiveId(session.id);
    set({ channels: next, activeChannelId: session.id });
    gitWriteChannel(session, `import channel ${shortId(session.id)} (${session.title})`);
  },

  selectChannel: (id) => {
    if (!get().channels.some((c) => c.id === id)) return;
    persistActiveId(id);
    set({ activeChannelId: id });
  },

  deleteChannel: (id) => {
    const prev = get().channels.find((c) => c.id === id);
    const next = get().channels.filter((c) => c.id !== id);
    persistChannels(next);
    let nextActive = get().activeChannelId;
    if (nextActive === id) {
      nextActive = next[0]?.id ?? null;
      persistActiveId(nextActive);
    }
    set({ channels: next, activeChannelId: nextActive });
    if (prev) gitDeleteChannel(id, prev.title);
  },

  append: (message) => {
    const { channels, activeChannelId } = get();
    if (!activeChannelId) return;
    const idx = channels.findIndex((c) => c.id === activeChannelId);
    if (idx < 0) return;
    const updated = appendMessage(channels[idx], message);
    // Move the updated channel to the top — recent activity bubbles up,
    // matches the Slack-style sidebar ordering.
    const next = [updated, ...channels.filter((_, i) => i !== idx)];
    persistChannels(next);
    set({ channels: next });
    const roleTag = message.role === "user" ? "user" :
                    message.role === "assistant" ? "AI" : "system";
    gitWriteChannel(updated, `update channel ${shortId(updated.id)} (+1 ${roleTag} msg)`);
  },

  setEncryptedKeys: (channelId, blob) => {
    const { channels } = get();
    const idx = channels.findIndex((c) => c.id === channelId);
    if (idx < 0) return;
    const updated: Session = {
      ...channels[idx],
      updated_at: new Date().toISOString(),
    };
    if (blob) updated.encrypted_keys = blob;
    else delete updated.encrypted_keys;
    const next = channels.map((c, i) => (i === idx ? updated : c));
    persistChannels(next);
    set({ channels: next });
    gitWriteChannel(updated, `update channel ${shortId(updated.id)} (${blob ? "attach" : "detach"} encrypted keys)`);
  },

  setSyncTarget: (channelId, target) => {
    const { channels } = get();
    const idx = channels.findIndex((c) => c.id === channelId);
    if (idx < 0) return;
    const updated: Session = {
      ...channels[idx],
      updated_at: new Date().toISOString(),
    };
    if (target) updated.sync_target = target;
    else delete updated.sync_target;
    const next = channels.map((c, i) => (i === idx ? updated : c));
    persistChannels(next);
    set({ channels: next });
    gitWriteChannel(updated, `update channel ${shortId(updated.id)} (${target ? "set" : "clear"} sync target)`);
  },

  setToolsEnabled: (channelId, enabled) => {
    const { channels } = get();
    const idx = channels.findIndex((c) => c.id === channelId);
    if (idx < 0) return;
    const updated: Session = {
      ...channels[idx],
      tools_enabled: enabled,
      updated_at: new Date().toISOString(),
    };
    const next = channels.map((c, i) => (i === idx ? updated : c));
    persistChannels(next);
    set({ channels: next });
    gitWriteChannel(updated, `update channel ${shortId(updated.id)} (tools ${enabled ? "on" : "off"})`);
  },

  replaceChannel: (channelId, session) => {
    const { channels } = get();
    const idx = channels.findIndex((c) => c.id === channelId);
    if (idx < 0) return;
    const next = channels.map((c, i) => (i === idx ? session : c));
    persistChannels(next);
    set({ channels: next });
  },

  setChannelMode: (channelId, mode) => {
    const { channels } = get();
    const idx = channels.findIndex((c) => c.id === channelId);
    if (idx < 0) return;
    const updated: Session = {
      ...channels[idx],
      mode,
      updated_at: new Date().toISOString(),
    };
    const next = channels.map((c, i) => (i === idx ? updated : c));
    persistChannels(next);
    set({ channels: next });
    gitWriteChannel(updated, `update channel ${shortId(updated.id)} (mode → ${mode})`);
  },

  setChannelDocument: (channelId, document) => {
    const { channels } = get();
    const idx = channels.findIndex((c) => c.id === channelId);
    if (idx < 0) return;
    const prev = channels[idx];
    // No-op when the doc didn't actually change (e.g. AI rewrote to
    // exactly the same text). Avoids polluting history with dupes.
    if ((prev.document ?? "") === document) return;
    const priorHistory = prev.document_history ?? [];
    const priorDoc = prev.document ?? "";
    // Only push the OLD document onto history if it was non-empty —
    // there's no point letting users undo back to a blank canvas.
    const nextHistory = priorDoc.trim().length > 0
      ? [priorDoc, ...priorHistory].slice(0, DOCUMENT_HISTORY_MAX)
      : priorHistory;
    const updated: Session = {
      ...prev,
      document,
      document_history: nextHistory,
      updated_at: new Date().toISOString(),
    };
    const next = channels.map((c, i) => (i === idx ? updated : c));
    persistChannels(next);
    set({ channels: next });
    const delta = document.length - priorDoc.length;
    const sign = delta >= 0 ? "+" : "";
    gitWriteChannel(updated, `edit whiteboard ${shortId(updated.id)} (${sign}${delta} chars)`);
  },

  attachDocument: (channelId, name, content, attachedBy) => {
    const { channels } = get();
    const idx = channels.findIndex((c) => c.id === channelId);
    if (idx < 0) return;
    const prev = channels[idx];
    const bytes = new TextEncoder().encode(content).length;
    if (bytes > ATTACHED_DOCUMENT_MAX_BYTES) {
      throw new Error(
        `"${name}" is ${(bytes / 1024).toFixed(0)}KB — single-doc cap is ` +
        `${ATTACHED_DOCUMENT_MAX_BYTES / 1024}KB. Trim or split before attaching.`,
      );
    }
    const existingAttached = prev.attached_documents ?? [];
    const totalAfter = existingAttached.reduce((sum, d) => sum + d.bytes, 0) + bytes;
    if (totalAfter > ATTACHED_DOCUMENTS_TOTAL_MAX_BYTES) {
      throw new Error(
        `Channel attached-doc total would be ${(totalAfter / 1024).toFixed(0)}KB — ` +
        `cap is ${ATTACHED_DOCUMENTS_TOTAL_MAX_BYTES / 1024}KB. Detach a doc first.`,
      );
    }
    const doc: AttachedDocument = {
      id: crypto.randomUUID(),
      name,
      content,
      bytes,
      attached_at: new Date().toISOString(),
      attached_by: attachedBy,
    };
    const updated: Session = {
      ...prev,
      attached_documents: [...existingAttached, doc],
      updated_at: new Date().toISOString(),
    };
    const next = channels.map((c, i) => (i === idx ? updated : c));
    persistChannels(next);
    set({ channels: next });
    gitWriteChannel(updated, `update channel ${shortId(updated.id)} (+attach ${name})`);
  },

  detachDocument: (channelId, attachedId) => {
    const { channels } = get();
    const idx = channels.findIndex((c) => c.id === channelId);
    if (idx < 0) return;
    const prev = channels[idx];
    const removed = (prev.attached_documents ?? []).find((d) => d.id === attachedId);
    const next_docs = (prev.attached_documents ?? []).filter((d) => d.id !== attachedId);
    const updated: Session = {
      ...prev,
      attached_documents: next_docs,
      updated_at: new Date().toISOString(),
    };
    const next = channels.map((c, i) => (i === idx ? updated : c));
    persistChannels(next);
    set({ channels: next });
    gitWriteChannel(updated, `update channel ${shortId(updated.id)} (-detach ${removed?.name ?? "doc"})`);
  },

  toggleReaction: (channelId, messageId, emoji, userName) => {
    const { channels } = get();
    const idx = channels.findIndex((c) => c.id === channelId);
    if (idx < 0) return;
    const ch = channels[idx];
    const msgIdx = ch.messages.findIndex((m) => m.id === messageId);
    if (msgIdx < 0) return;
    const msg = ch.messages[msgIdx];
    const reactions = { ...(msg.reactions ?? {}) };
    const current = reactions[emoji] ?? [];
    const i = current.indexOf(userName);
    if (i >= 0) {
      const next = current.filter((u) => u !== userName);
      if (next.length === 0) delete reactions[emoji];
      else reactions[emoji] = next;
    } else {
      reactions[emoji] = [...current, userName];
    }
    const updatedMsg = { ...msg, reactions: Object.keys(reactions).length > 0 ? reactions : undefined };
    const updatedMessages = ch.messages.map((m, i) => (i === msgIdx ? updatedMsg : m));
    const updatedChannel: Session = {
      ...ch,
      messages: updatedMessages,
      updated_at: new Date().toISOString(),
    };
    const next = channels.map((c, i) => (i === idx ? updatedChannel : c));
    persistChannels(next);
    set({ channels: next });
    const verb = i >= 0 ? "remove" : "add";
    gitWriteChannel(updatedChannel, `update channel ${shortId(updatedChannel.id)} (${verb} ${emoji} reaction by ${userName})`);
  },

  duplicateChannel: (channelId) => {
    const { channels } = get();
    const src = channels.find((c) => c.id === channelId);
    if (!src) return;
    const now = new Date().toISOString();
    const copy: Session = {
      ...src,
      id: crypto.randomUUID(),
      title: `${src.title} — copy`,
      created_at: now,
      updated_at: now,
      // Reset local navigation/state that shouldn't carry across a fork
      pinned: false,
      sync_target: undefined,
      encrypted_keys: undefined,
      total_tokens: undefined,
      document_history: undefined,
      // Re-stamp message ids so the copy doesn't share identity with
      // the original — otherwise edits to one would dedupe against
      // the other via the channel-sync merge-by-id semantics.
      messages: src.messages.map((m) => ({ ...m, id: crypto.randomUUID() })),
      // Attached docs get fresh ids too so the same shape applies.
      attached_documents: (src.attached_documents ?? []).map((d) => ({
        ...d,
        id: crypto.randomUUID(),
      })),
    };
    const next = [copy, ...channels];
    persistChannels(next);
    persistActiveId(copy.id);
    set({ channels: next, activeChannelId: copy.id });
    gitWriteChannel(copy, `create channel ${shortId(copy.id)} (duplicate of ${shortId(src.id)})`);
  },

  toggleArchived: (channelId) => {
    const { channels } = get();
    const idx = channels.findIndex((c) => c.id === channelId);
    if (idx < 0) return;
    const updated: Session = {
      ...channels[idx],
      archived: !channels[idx].archived,
      // Archived channels can stay pinned conceptually, but it looks
      // weird to have a pinned-but-archived channel in the "show
      // archived" view. Clear the pin on archive — re-pin on unarchive
      // is one click.
      pinned: !channels[idx].archived ? false : channels[idx].pinned,
    };
    const next = channels.map((c, i) => (i === idx ? updated : c));
    persistChannels(next);
    set({ channels: next });
    gitWriteChannel(updated, `update channel ${shortId(updated.id)} (${updated.archived ? "archive" : "unarchive"})`);
  },

  togglePinned: (channelId) => {
    const { channels } = get();
    const idx = channels.findIndex((c) => c.id === channelId);
    if (idx < 0) return;
    const updated: Session = {
      ...channels[idx],
      pinned: !channels[idx].pinned,
      // Same defensive update_at-skip as recordUsage — pinning is
      // navigation state, not content, so it shouldn't shuffle the
      // sidebar's most-recent ordering on top of the pin sort.
    };
    const next = channels.map((c, i) => (i === idx ? updated : c));
    persistChannels(next);
    set({ channels: next });
    gitWriteChannel(updated, `update channel ${shortId(updated.id)} (${updated.pinned ? "pin" : "unpin"})`);
  },

  recordUsage: (channelId, prompt, completion) => {
    const { channels } = get();
    const idx = channels.findIndex((c) => c.id === channelId);
    if (idx < 0) return;
    const prev = channels[idx].total_tokens ?? { prompt: 0, completion: 0, turns: 0 };
    const updated: Session = {
      ...channels[idx],
      total_tokens: {
        prompt: prev.prompt + prompt,
        completion: prev.completion + completion,
        turns: prev.turns + 1,
      },
      // Don't bump updated_at — usage tracking shouldn't shuffle the
      // sidebar's most-recent ordering; that's for content changes.
    };
    const next = channels.map((c, i) => (i === idx ? updated : c));
    persistChannels(next);
    set({ channels: next });
  },

  setBrainstormMoshPit: (channelId, on) => {
    const { channels } = get();
    const idx = channels.findIndex((c) => c.id === channelId);
    if (idx < 0) return;
    const updated: Session = {
      ...channels[idx],
      brainstorm_mp_enabled: on,
      updated_at: new Date().toISOString(),
    };
    const next = channels.map((c, i) => (i === idx ? updated : c));
    persistChannels(next);
    set({ channels: next });
    gitWriteChannel(updated, `update channel ${shortId(updated.id)} (brainstorm-MP ${on ? "on" : "off"})`);
  },

  undoDocument: (channelId) => {
    const { channels } = get();
    const idx = channels.findIndex((c) => c.id === channelId);
    if (idx < 0) return;
    const prev = channels[idx];
    const history = prev.document_history ?? [];
    if (history.length === 0) return;
    const [restore, ...rest] = history;
    const updated: Session = {
      ...prev,
      document: restore,
      document_history: rest,
      updated_at: new Date().toISOString(),
    };
    const next = channels.map((c, i) => (i === idx ? updated : c));
    persistChannels(next);
    set({ channels: next });
    gitWriteChannel(updated, `reverse whiteboard ${shortId(updated.id)} (undo, ${rest.length} steps remain)`);
  },
}));
